import { useState, useRef, useEffect, useMemo, useCallback, type JSX } from 'react';
import { format } from 'date-fns';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useFloating, offset, flip, shift, autoUpdate, FloatingPortal } from '@floating-ui/react';
import { useResizeObserver, useTimelineData, useMonthMarkers, useSessionState } from '@/hooks';
import { ZoomControls } from '@/components/atoms';
import { TimelineRow } from '@/components/molecules/TimelineRow';
import { TimelineHeader, TIMELINE_HEADER_HEIGHT } from '@/components/molecules/TimelineHeader';
import { TimelineLegend } from '@/components/molecules/TimelineLegend';
import { TimelineGroupHeader } from '@/components/molecules/TimelineGroupHeader';
import { COLLAPSED_GROUP_HEIGHT } from '@/components/molecules/TimelineGroupHeader.constants';
import { TimelineLabelColumn } from '@/components/molecules/TimelineLabelColumn';
import { ColorSchemeSelector } from '@/components/molecules/ColorSchemeSelector';
import { GroupModeToggle } from '@/components/molecules/GroupModeToggle';
import { DependencyPopover } from '@/components/molecules/DependencyPopover';
import { DependencyOverlay } from '@/components/molecules/DependencyOverlay';
import { DependencyPathLegend } from '@/components/molecules/DependencyPathLegend';
import { PathFilterToggle } from '@/components/molecules/PathFilterToggle';
import {
  FALLBACK_COLOR,
  TIMELINE_LAYOUT,
  DEFAULT_COLOR_SCHEME,
  getColorScheme,
  SEGMENT_INFO,
  ZOOM_CONFIG,
  LABEL_CONFIG,
  DEPENDENCY_PATH_COLORS,
  getDepthOpacity,
} from '@/constants/timeline';
import { getDependencyPathWithInfo } from '@/lib/utils';
import type {
  ProcessedDeliverableResult,
  DependencyMap,
  DeliverableInfo,
  PathNodeInfo,
  PathFilterMode,
} from '@/types/api';
import type {
  DeliverableTimelineData,
  GroupMode,
  TooltipSegment,
  ColorScheme,
  DeliverablePosition,
  DependencyConnection,
} from '@/types/timeline';

export interface FeatureTimelineProps {
  deliverables: ProcessedDeliverableResult[];
  /** Map of team name to color (from database). Required - colors are first-class data. */
  teamColors: Record<string, string>;
  /** Current group mode (controlled via URL params) */
  groupMode: GroupMode;
  /** Callback when group mode changes */
  onGroupModeChange: (mode: GroupMode) => void;
  /** Feature names in configuration order (from simulation setup) */
  featureOrder: string[];
  /** Feature due dates (feature name → due date) */
  featureDueDates?: Map<string, Date | null>;
  /** Simulation ID for session storage key (optional - uses local state if not provided) */
  simulationId?: string;
  /** Effective simulation start date; a due date before this predates the forecast (S3 warning). */
  simulationStartDate: Date;
  /** Callback when a deliverable label is clicked */
  onDeliverableClick?: (data: DeliverableTimelineData) => void;
  /** Dependency map for showing predecessor/dependent relationships */
  dependencyMap?: DependencyMap;
  /** Lookup map of deliverable info for popover display */
  deliverableLookup?: Map<string, DeliverableInfo>;
}

/**
 * Feature Timeline organism - visualizes deliverable timelines with confidence ranges
 *
 * Displays Monte Carlo simulation results as a Gantt-like chart with probability segments.
 * Supports grouping by team or feature, multiple color schemes, and interactive tooltips.
 *
 * Architecture:
 * - HTML label column (sticky left) + SVG chart area (horizontally scrollable)
 * - Zoom controls for horizontal scaling (0.5x to 3x)
 * - Resizable label column (100px to 400px)
 */
export function FeatureTimeline({
  deliverables,
  teamColors,
  groupMode,
  onGroupModeChange,
  featureOrder,
  featureDueDates,
  simulationId,
  simulationStartDate,
  onDeliverableClick,
  dependencyMap,
  deliverableLookup,
}: FeatureTimelineProps) {
  // Refs for scroll sync between label column and chart area
  const labelScrollRef = useRef<HTMLDivElement>(null);
  const chartScrollRef = useRef<HTMLDivElement>(null);
  const isScrollSyncing = useRef(false);

  // Use chart scroll container for width measurement
  const { width: containerWidth } = useResizeObserver(chartScrollRef);

  // Zoom, label width, and color scheme state (persisted in session storage)
  const storagePrefix = `timeline-${simulationId ?? 'default'}`;
  const [zoom, setZoom] = useSessionState<number>(`${storagePrefix}-zoom`, ZOOM_CONFIG.DEFAULT);
  const [labelWidth, setLabelWidth] = useSessionState<number>(
    `${storagePrefix}-labelWidth`,
    LABEL_CONFIG.DEFAULT_WIDTH,
  );
  const [colorSchemeId, setColorSchemeId] = useSessionState<string>(
    `${storagePrefix}-colorScheme`,
    DEFAULT_COLOR_SCHEME,
  );

  // Manage collapsed groups state in session storage
  const collapsedStorageKey = `${storagePrefix}-collapsed-${groupMode}`;
  const [collapsedGroups, setCollapsedGroups] = useSessionState<string[]>(collapsedStorageKey, []);

  // Derive expanded groups from collapsed groups
  const collapsedSet = useMemo(() => new Set(collapsedGroups), [collapsedGroups]);

  // Tooltip state
  const [hoveredSegment, setHoveredSegment] = useState<TooltipSegment | null>(null);
  const [referenceElement, setReferenceElement] = useState<SVGElement | null>(null);

  // Dependency popover state
  const [activeDependencyId, setActiveDependencyId] = useState<string | null>(null);
  const [dependencyBadgeRef, setDependencyBadgeRef] = useState<HTMLElement | null>(null);

  // Selected deliverable for dependency line visualization (click-based, not hover)
  // Lines persist until user clicks elsewhere on the chart
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);

  // Path filter mode: dim non-path items or hide them completely
  const [pathFilterMode, setPathFilterMode] = useSessionState<PathFilterMode>(`${storagePrefix}-pathFilter`, 'dim');

  // ESC key handler to clear dependency selection
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedConnectionId) {
        setSelectedConnectionId(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [selectedConnectionId]);

  const { refs, floatingStyles } = useFloating({
    open: hoveredSegment !== null,
    placement: 'top',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  // Destructure the stable callback-ref setters so JSX references plain functions
  // rather than reading properties off the `refs` object during render.
  const { setReference, setFloating } = refs;

  // Sync the (dynamic, hover-driven) reference element with floating-ui.
  useEffect(() => {
    setReference(referenceElement);
  }, [referenceElement, setReference]);

  // Get active color scheme
  const activeColors = useMemo<ColorScheme>(() => getColorScheme(colorSchemeId), [colorSchemeId]);

  // Process deliverable data
  const { groupedData, flatData } = useTimelineData({
    deliverables,
    groupMode,
    featureOrder,
    dependencyMap,
    featureDueDates,
  });

  // Toggle a single group's collapsed state
  const toggleGroup = useCallback(
    (groupName: string) => {
      if (collapsedSet.has(groupName)) {
        // Expand: remove from collapsed list
        setCollapsedGroups(collapsedGroups.filter((g) => g !== groupName));
      } else {
        // Collapse: add to collapsed list
        setCollapsedGroups([...collapsedGroups, groupName]);
      }
    },
    [collapsedGroups, collapsedSet, setCollapsedGroups],
  );

  // Expand/collapse all groups
  const expandAll = useCallback(() => {
    setCollapsedGroups([]);
  }, [setCollapsedGroups]);

  const collapseAll = useCallback(() => {
    const allGroupNames = groupedData.map((g) => g.groupName);
    setCollapsedGroups(allGroupNames);
  }, [groupedData, setCollapsedGroups]);

  // Check if all groups are expanded (for toggle button)
  const allExpanded = groupedData.every((g) => !collapsedSet.has(g.groupName));

  // Calculate chart dimensions - containerWidth is measured on chart scroll container
  // which already excludes the label column (they're separate in the flex layout)
  const chartWidth = Math.max(containerWidth, TIMELINE_LAYOUT.MIN_CHART_WIDTH);
  const scaledChartWidth = chartWidth * zoom;

  // Calculate month markers, quarter markers, and date range
  const { monthMarkers, quarterMarkers, dateToX, minDate } = useMonthMarkers({
    flatData,
    chartWidth,
    simulationStartDate,
  });

  // Create a scaled dateToX function for rendering
  const scaledDateToX = useCallback(
    (date: Date | null): number => {
      return dateToX(date) * zoom;
    },
    [dateToX, zoom],
  );

  // Get team color from the provided map
  const getTeamColor = useCallback((teamName: string): string => teamColors[teamName] ?? FALLBACK_COLOR, [teamColors]);

  // Handle segment hover
  const handleSegmentHover = useCallback((segment: TooltipSegment | null, element: SVGElement | null) => {
    setHoveredSegment(segment);
    setReferenceElement(element);
  }, []);

  // Handle dependency badge click
  const handleDependencyClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, deliverableId: string) => {
      if (activeDependencyId === deliverableId) {
        // Toggle off if clicking the same badge
        setActiveDependencyId(null);
        setDependencyBadgeRef(null);
      } else {
        setActiveDependencyId(deliverableId);
        setDependencyBadgeRef(event.currentTarget);
      }
    },
    [activeDependencyId],
  );

  // Handle dependency navigation (scrolling to a deliverable)
  const handleDependencyNavigate = useCallback((deliverableId: string) => {
    // Close the popover
    setActiveDependencyId(null);
    setDependencyBadgeRef(null);

    // Find the row element with the matching data-deliverable-id
    const targetRow = labelScrollRef.current?.querySelector(`[data-deliverable-id="${deliverableId}"]`);
    if (targetRow && labelScrollRef.current) {
      // Scroll to bring the row into view
      targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Add highlight animation
      targetRow.classList.add('dependency-highlight');
      setTimeout(() => {
        targetRow.classList.remove('dependency-highlight');
      }, 2000);
    }
  }, []);

  // Close dependency popover
  const handleCloseDependencyPopover = useCallback(() => {
    setActiveDependencyId(null);
    setDependencyBadgeRef(null);
  }, []);

  // Get active dependency info for popover
  const activeDependencyInfo = useMemo(() => {
    if (!activeDependencyId || !deliverableLookup || !dependencyMap) {
      return null;
    }

    const deliverable = deliverableLookup.get(activeDependencyId);
    if (!deliverable) return null;

    const predecessorIds = dependencyMap.predecessors[activeDependencyId] ?? [];
    const dependentIds = dependencyMap.dependents[activeDependencyId] ?? [];

    const incoming = predecessorIds
      .map((id) => deliverableLookup.get(id))
      .filter((info): info is DeliverableInfo => info !== undefined);

    const outgoing = dependentIds
      .map((id) => deliverableLookup.get(id))
      .filter((info): info is DeliverableInfo => info !== undefined);

    return { deliverable, incoming, outgoing };
  }, [activeDependencyId, deliverableLookup, dependencyMap]);

  // Sync vertical scroll between label column and chart area
  const handleLabelScroll = useCallback(() => {
    if (isScrollSyncing.current) return;
    isScrollSyncing.current = true;
    if (labelScrollRef.current && chartScrollRef.current) {
      chartScrollRef.current.scrollTop = labelScrollRef.current.scrollTop;
    }
    requestAnimationFrame(() => {
      isScrollSyncing.current = false;
    });
  }, []);

  const handleChartScroll = useCallback(() => {
    if (isScrollSyncing.current) return;
    isScrollSyncing.current = true;
    if (labelScrollRef.current && chartScrollRef.current) {
      labelScrollRef.current.scrollTop = chartScrollRef.current.scrollTop;
    }
    requestAnimationFrame(() => {
      isScrollSyncing.current = false;
    });
  }, []);

  // Compute the full dependency path with direction and depth info
  // NOTE: This must be computed BEFORE chartBodyHeight and deliverablePositions
  // because hide mode affects row positions and chart height
  const pathInfo = useMemo<Map<string, PathNodeInfo>>(() => {
    if (!selectedConnectionId || !dependencyMap) {
      return new Map();
    }
    return getDependencyPathWithInfo(selectedConnectionId, dependencyMap);
  }, [selectedConnectionId, dependencyMap]);

  // Derive simple set of path IDs for quick lookups
  const pathDeliverableIds = useMemo<Set<string>>(() => {
    return new Set(pathInfo.keys());
  }, [pathInfo]);

  // Check if path visualization is active
  const isPathActive = pathDeliverableIds.size > 0;

  // Helper to check if a row should be hidden (used in position and height computation)
  const isRowHidden = useCallback(
    (deliverableId: string | null): boolean => {
      if (!deliverableId || pathFilterMode !== 'hide' || pathDeliverableIds.size === 0) return false;
      return !pathDeliverableIds.has(deliverableId);
    },
    [pathFilterMode, pathDeliverableIds],
  );

  // Whether to hide group headers (in hide mode with active path)
  const hideGroupHeaders = isPathActive && pathFilterMode === 'hide';

  // Calculate total height (including top padding for due date badges)
  // NOTE: This accounts for hidden rows and group headers in hide mode
  const chartBodyHeight = useMemo(() => {
    let height = TIMELINE_LAYOUT.CHART_TOP_PADDING;
    let isFirstVisibleGroup = true;

    for (let i = 0; i < groupedData.length; i++) {
      const group = groupedData[i]!;
      const isExpanded = !collapsedSet.has(group.groupName);
      const visibleItems = group.items.filter((data) => !isRowHidden(data.deliverableId));

      // Skip entirely empty groups in hide mode
      if (hideGroupHeaders && visibleItems.length === 0) {
        continue;
      }

      // Add separator height between groups (not before first visible group)
      if (!isFirstVisibleGroup && !hideGroupHeaders) {
        height += TIMELINE_LAYOUT.GROUP_SEPARATOR_HEIGHT;
      }
      isFirstVisibleGroup = false;

      if (!hideGroupHeaders) {
        if (isExpanded) {
          // Expanded: header row + visible item rows only
          height += COLLAPSED_GROUP_HEIGHT;
          height += visibleItems.length * TIMELINE_LAYOUT.ROW_HEIGHT;
        } else {
          // Collapsed: header row with inline aggregate bar
          height += COLLAPSED_GROUP_HEIGHT;
        }
      } else {
        // Headers hidden: just the visible item rows
        height += visibleItems.length * TIMELINE_LAYOUT.ROW_HEIGHT;
      }
    }

    return height;
  }, [groupedData, collapsedSet, isRowHidden, hideGroupHeaders]);

  // Compute deliverable positions for dependency line rendering
  // Maps deliverable ID to its position (y center, P50 start X, P50 finish X)
  // NOTE: This accounts for hidden rows and group headers in hide mode
  const deliverablePositions = useMemo<Map<string, DeliverablePosition>>(() => {
    const positions = new Map<string, DeliverablePosition>();
    let currentY = TIMELINE_LAYOUT.CHART_TOP_PADDING;
    const barCenterOffset = TIMELINE_LAYOUT.ROW_HEIGHT / 2; // Center of the row
    let isFirstVisibleGroup = true;

    for (let i = 0; i < groupedData.length; i++) {
      const group = groupedData[i]!;
      const isExpanded = !collapsedSet.has(group.groupName);
      const visibleItems = group.items.filter((data) => !isRowHidden(data.deliverableId));

      // Skip entirely empty groups in hide mode
      if (hideGroupHeaders && visibleItems.length === 0) {
        continue;
      }

      // Account for group separator (not before first visible group, and not when headers hidden)
      if (!isFirstVisibleGroup && !hideGroupHeaders) {
        currentY += TIMELINE_LAYOUT.GROUP_SEPARATOR_HEIGHT;
      }
      isFirstVisibleGroup = false;

      if (!hideGroupHeaders) {
        currentY += COLLAPSED_GROUP_HEIGHT;

        if (!isExpanded) {
          // For collapsed groups, add all items to positions but mark them as collapsed
          // They'll use the group header position for connection endpoints
          const groupY = currentY - COLLAPSED_GROUP_HEIGHT + barCenterOffset;
          for (const data of group.items) {
            if (data.deliverableId && !isRowHidden(data.deliverableId)) {
              positions.set(data.deliverableId, {
                id: data.deliverableId,
                y: groupY,
                startX: data.startP50 ? scaledDateToX(data.startP50) : 0,
                finishX: data.finishP50 ? scaledDateToX(data.finishP50) : 0,
                isCollapsed: true,
                groupName: group.groupName,
              });
            }
          }
          continue;
        }
      }

      // Items in expanded group (or all items when headers are hidden)
      for (const data of group.items) {
        // Skip hidden rows - they don't contribute to Y position
        if (isRowHidden(data.deliverableId)) {
          continue;
        }

        if (data.deliverableId) {
          positions.set(data.deliverableId, {
            id: data.deliverableId,
            y: currentY + barCenterOffset,
            startX: data.startP50 ? scaledDateToX(data.startP50) : 0,
            finishX: data.finishP50 ? scaledDateToX(data.finishP50) : 0,
            isCollapsed: false,
            groupName: group.groupName,
          });
        }
        currentY += TIMELINE_LAYOUT.ROW_HEIGHT;
      }
    }

    return positions;
  }, [groupedData, collapsedSet, scaledDateToX, isRowHidden, hideGroupHeaders]);

  // Compute active dependency connections for the FULL path (transitive)
  // This shows ALL connections in the dependency chain with direction-based coloring
  const activeConnections = useMemo<DependencyConnection[]>(() => {
    if (!selectedConnectionId || !dependencyMap || pathInfo.size === 0) {
      return [];
    }

    const connections: DependencyConnection[] = [];

    // Iterate through all deliverables in the path and add their connections
    // Only add connections where BOTH endpoints are in the path
    for (const [deliverableId, nodeInfo] of pathInfo) {
      const sourcePos = deliverablePositions.get(deliverableId);
      if (!sourcePos) continue;

      // Add outgoing connections (this item blocks others in the path)
      const dependentIds = dependencyMap.dependents[deliverableId] ?? [];
      for (const depId of dependentIds) {
        // Only include if the dependent is also in the path
        const depNodeInfo = pathInfo.get(depId);
        if (!depNodeInfo) continue;

        const depPos = deliverablePositions.get(depId);
        if (depPos) {
          // Connection type is based on direction relative to selected item
          // If source is blocking/selected, the connection goes toward following items
          // Use the source node's direction for line coloring
          const connectionDirection = nodeInfo.direction === 'selected' ? 'following' : nodeInfo.direction;

          // Calculate opacity based on the depth of the SOURCE node
          const opacity = getDepthOpacity(nodeInfo.depth);

          connections.push({
            fromId: deliverableId,
            toId: depId,
            type: connectionDirection === 'blocking' ? 'incoming' : 'outgoing',
            from: { x: sourcePos.finishX, y: sourcePos.y },
            to: { x: depPos.startX, y: depPos.y },
            isCollapsed: depPos.isCollapsed,
            // Extended properties for enhanced visualization
            direction: connectionDirection,
            opacity,
          });
        }
      }
    }

    return connections;
  }, [selectedConnectionId, dependencyMap, deliverablePositions, pathInfo]);

  // Handle row click for dependency line visualization (click to show, click elsewhere to hide)
  const handleRowConnectionClick = useCallback((deliverableId: string | null) => {
    // Toggle: if clicking the same row, deselect; otherwise select the new one
    setSelectedConnectionId((current) => (current === deliverableId ? null : deliverableId));
  }, []);

  // Handle click on SVG background to clear selection
  const handleSvgBackgroundClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    // Only clear if clicking directly on the SVG (not on a child element)
    if (e.target === e.currentTarget) {
      setSelectedConnectionId(null);
    }
  }, []);

  // Check if a deliverable is in the dependency path (highlighted)
  const isConnectionTarget = useCallback(
    (deliverableId: string | null): boolean => {
      if (!deliverableId || pathDeliverableIds.size === 0) return false;
      return pathDeliverableIds.has(deliverableId);
    },
    [pathDeliverableIds],
  );

  // Check if a deliverable should be dimmed (path is active but this item is not in it)
  const shouldDimRow = useCallback(
    (deliverableId: string | null): boolean => {
      if (!deliverableId || pathDeliverableIds.size === 0) return false;
      return !pathDeliverableIds.has(deliverableId);
    },
    [pathDeliverableIds],
  );

  // Check if a deliverable should be hidden (hide mode + not in path)
  const shouldHideRow = useCallback(
    (deliverableId: string | null): boolean => {
      if (pathFilterMode !== 'hide') return false;
      return shouldDimRow(deliverableId);
    },
    [pathFilterMode, shouldDimRow],
  );

  // Get path node info for a deliverable (for color/opacity calculations)
  const getPathNodeInfo = useCallback(
    (deliverableId: string | null): PathNodeInfo | null => {
      if (!deliverableId) return null;
      return pathInfo.get(deliverableId) ?? null;
    },
    [pathInfo],
  );

  // Get the highlight color for a deliverable based on its path direction
  const getPathHighlightColor = useCallback(
    (deliverableId: string | null): string | null => {
      const nodeInfo = getPathNodeInfo(deliverableId);
      if (!nodeInfo) return null;
      return DEPENDENCY_PATH_COLORS[nodeInfo.direction].line;
    },
    [getPathNodeInfo],
  );

  if (deliverables.length === 0) {
    return <div className="py-12 text-center text-text-muted">No deliverable data to display</div>;
  }

  return (
    <div className="space-y-4">
      {/* Title and Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h3 className="text-lg font-semibold text-text-primary">Feature Timeline with Confidence Ranges</h3>

        <div className="flex flex-wrap items-center gap-4">
          {/* Zoom Controls */}
          <ZoomControls zoom={zoom} onZoomChange={setZoom} />

          {/* Expand/Collapse All Toggle */}
          {groupedData.length > 1 && (
            <button
              onClick={allExpanded ? collapseAll : expandAll}
              className="
                flex items-center gap-1 px-2 py-1 text-sm text-text-secondary transition-colors
                hover:text-text-primary
              "
              title={allExpanded ? 'Collapse all groups' : 'Expand all groups'}
            >
              {allExpanded ? (
                <>
                  <ChevronDown className="size-4" />
                  <span
                    className="
                      hidden
                      sm:inline
                    "
                  >
                    Collapse All
                  </span>
                </>
              ) : (
                <>
                  <ChevronRight className="size-4" />
                  <span
                    className="
                      hidden
                      sm:inline
                    "
                  >
                    Expand All
                  </span>
                </>
              )}
            </button>
          )}
          <GroupModeToggle value={groupMode} onChange={onGroupModeChange} />
        </div>
      </div>

      {/* Legend with Color Scheme Selector */}
      <div className="flex flex-wrap items-center gap-4">
        <TimelineLegend colors={activeColors} />
        <ColorSchemeSelector value={colorSchemeId} onChange={setColorSchemeId} className="ml-auto" />
      </div>

      {/* Dependency Path Legend and Filter (only visible when path is active) */}
      <div className="flex flex-wrap items-center gap-4">
        <DependencyPathLegend isActive={isPathActive} />
        <PathFilterToggle
          value={pathFilterMode}
          onChange={setPathFilterMode}
          isActive={isPathActive}
          className="ml-auto"
        />
      </div>

      {/* Timeline Chart - Two-column layout: fixed labels + scrollable chart */}
      <div className="overflow-hidden rounded-lg border border-border-default/50 bg-bg-base">
        <div className="flex" style={{ maxHeight: 'calc(70vh - 40px)' }}>
          {/* Left: Label column - scrolls only vertically, synced with chart */}
          <div
            ref={labelScrollRef}
            onScroll={handleLabelScroll}
            className="scrollbar-hide shrink-0 overflow-x-hidden overflow-y-auto border-r border-border-default/30"
            style={{ width: labelWidth }}
          >
            {/* Header spacer - matches chart header height (SVG 44px + 1px border outside) */}
            <div
              className="
                sticky top-0 z-20 box-content rounded-tl-lg border-b border-border-default/50 bg-bg-base/95
                backdrop-blur-sm
                dark:bg-bg-muted/95
              "
              style={{ height: TIMELINE_HEADER_HEIGHT }}
            />
            {/* Labels body - uses internal component without sticky (parent handles it) */}
            <TimelineLabelColumn
              labelWidth={labelWidth}
              onLabelWidthChange={setLabelWidth}
              groupedData={groupedData}
              collapsedSet={collapsedSet}
              onToggleGroup={toggleGroup}
              groupMode={groupMode}
              getTeamColor={getTeamColor}
              onDeliverableClick={onDeliverableClick}
              simulationStartDate={simulationStartDate}
              activeDependencyId={activeDependencyId}
              onDependencyClick={dependencyMap && deliverableLookup ? handleDependencyClick : undefined}
              onRowConnectionClick={dependencyMap ? handleRowConnectionClick : undefined}
              selectedConnectionId={selectedConnectionId}
              shouldDimRow={dependencyMap ? shouldDimRow : undefined}
              shouldHideRow={dependencyMap ? shouldHideRow : undefined}
              getPathHighlightColor={dependencyMap ? getPathHighlightColor : undefined}
              hideGroupHeaders={hideGroupHeaders}
            />
            {/* Bottom spacer to compensate for chart's horizontal scrollbar height */}
            <div className="h-2 shrink-0" />
          </div>

          {/* Right: Chart area - scrolls both ways */}
          <div ref={chartScrollRef} onScroll={handleChartScroll} className="flex-1 overflow-auto">
            {/* Sticky header */}
            <div
              className="
                sticky top-0 z-20 rounded-tr-lg border-b border-border-default/50 bg-bg-base/95 backdrop-blur-sm
                dark:bg-bg-muted/95
              "
              style={{ width: scaledChartWidth }}
            >
              <TimelineHeader
                chartWidth={chartWidth}
                monthMarkers={monthMarkers}
                quarterMarkers={quarterMarkers}
                zoom={zoom}
              />
            </div>

            {/* Chart body */}
            <div style={{ width: scaledChartWidth }}>
              <svg
                width={scaledChartWidth}
                height={chartBodyHeight}
                className="cursor-pointer font-sans"
                onClick={handleSvgBackgroundClick}
              >
                {/* Quarter column backgrounds - alternating subtle fill */}
                {quarterMarkers.map((quarter, i) => (
                  <rect
                    key={`qbg-${quarter.year}-${quarter.quarter}`}
                    x={quarter.startX * zoom}
                    y={0}
                    width={(quarter.endX - quarter.startX) * zoom}
                    height={chartBodyHeight}
                    className={i % 2 === 0 ? 'fill-bg-subtle' : `fill-transparent`}
                    fillOpacity={i % 2 === 0 ? 0.15 : 0}
                  />
                ))}
                {/* Quarter boundaries - stronger gridlines */}
                {quarterMarkers.map((quarter) => (
                  <line
                    key={`q-${quarter.year}-${quarter.quarter}`}
                    x1={quarter.startX * zoom}
                    y1={0}
                    x2={quarter.startX * zoom}
                    y2={chartBodyHeight}
                    className="stroke-border-default"
                    strokeWidth={1}
                    strokeDasharray="4,4"
                  />
                ))}
                {/* Month gridlines - visible but lighter than quarter lines */}
                {monthMarkers.map((marker, i) => {
                  // Skip if this is a quarter boundary (already drawn above)
                  const isQuarterStart = marker.date.getMonth() % 3 === 0;
                  if (isQuarterStart) return null;

                  return (
                    <line
                      key={i}
                      x1={marker.x * zoom}
                      y1={0}
                      x2={marker.x * zoom}
                      y2={chartBodyHeight}
                      className="stroke-border-default"
                      strokeWidth={1}
                      strokeDasharray="2,4"
                      opacity={0.5}
                    />
                  );
                })}

                {/* Groups and Rows */}
                {(() => {
                  let currentY = TIMELINE_LAYOUT.CHART_TOP_PADDING;
                  const elements: JSX.Element[] = [];
                  let isFirstVisibleGroup = true;

                  groupedData.forEach((group, groupIndex) => {
                    const groupColor = groupMode === 'team' ? getTeamColor(group.groupName) : '#6b7280';
                    const isExpanded = !collapsedSet.has(group.groupName);
                    const visibleItems = group.items.filter((data) => !shouldHideRow(data.deliverableId));

                    // Skip entirely empty groups in hide mode
                    if (hideGroupHeaders && visibleItems.length === 0) {
                      return;
                    }

                    // Add separator between groups (not before first visible group, not when headers hidden)
                    if (!isFirstVisibleGroup && !hideGroupHeaders) {
                      elements.push(
                        <rect
                          key={`separator-${groupIndex}`}
                          x={0}
                          y={currentY}
                          width={scaledChartWidth}
                          height={TIMELINE_LAYOUT.GROUP_SEPARATOR_HEIGHT}
                          className="fill-bg-subtle"
                        />,
                        <line
                          key={`separator-top-${groupIndex}`}
                          x1={0}
                          y1={currentY}
                          x2={scaledChartWidth}
                          y2={currentY}
                          className="stroke-border-subtle"
                          strokeWidth={1}
                          opacity={0.3}
                        />,
                        <line
                          key={`separator-bottom-${groupIndex}`}
                          x1={0}
                          y1={currentY + TIMELINE_LAYOUT.GROUP_SEPARATOR_HEIGHT}
                          x2={scaledChartWidth}
                          y2={currentY + TIMELINE_LAYOUT.GROUP_SEPARATOR_HEIGHT}
                          className="stroke-border-subtle"
                          strokeWidth={1}
                          opacity={0.3}
                        />,
                      );
                      currentY += TIMELINE_LAYOUT.GROUP_SEPARATOR_HEIGHT;
                    }
                    isFirstVisibleGroup = false;

                    // Group header (shows aggregate bars for both collapsed and expanded states)
                    // Hidden when in hide mode with active path
                    if (!hideGroupHeaders) {
                      elements.push(
                        <TimelineGroupHeader
                          key={`group-${groupIndex}`}
                          groupName={group.groupName}
                          itemCount={group.items.length}
                          y={currentY}
                          totalWidth={scaledChartWidth}
                          color={groupColor}
                          aggregateTimeline={group.aggregateTimeline}
                          activeColors={activeColors}
                          colorSchemeId={colorSchemeId}
                          dateToX={scaledDateToX}
                          onSegmentHover={handleSegmentHover}
                        />,
                      );
                      currentY += COLLAPSED_GROUP_HEIGHT;

                      if (!isExpanded) {
                        return; // Skip rendering individual items when collapsed
                      }
                    }

                    // Items in group (only rendered if expanded, no header, or headers hidden)
                    group.items.forEach((data) => {
                      // Skip hidden rows in hide mode
                      if (shouldHideRow(data.deliverableId)) {
                        return;
                      }

                      const itemColor = groupMode === 'team' ? groupColor : getTeamColor(data.teamName);
                      const pathHighlightColor = getPathHighlightColor(data.deliverableId);

                      elements.push(
                        <TimelineRow
                          key={data.id}
                          data={data}
                          y={currentY}
                          totalWidth={scaledChartWidth}
                          color={itemColor}
                          activeColors={activeColors}
                          dateToX={scaledDateToX}
                          onSegmentHover={handleSegmentHover}
                          timelineStartDate={minDate}
                          onRowConnectionClick={dependencyMap ? handleRowConnectionClick : undefined}
                          isConnectionTarget={isConnectionTarget(data.deliverableId)}
                          isDimmed={shouldDimRow(data.deliverableId)}
                          pathHighlightColor={pathHighlightColor}
                          isSelected={data.deliverableId === selectedConnectionId}
                        />,
                      );

                      currentY += TIMELINE_LAYOUT.ROW_HEIGHT;
                    });
                  });

                  return elements;
                })()}

                {/* Dependency connection lines overlay */}
                {activeConnections.length > 0 && (
                  <DependencyOverlay
                    connections={activeConnections}
                    width={scaledChartWidth}
                    height={chartBodyHeight}
                    selectedConnectionId={selectedConnectionId}
                  />
                )}
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Date axis label */}
      <div className="text-center text-sm text-text-muted">Date</div>

      {/* Tooltip */}
      {hoveredSegment && (
        <FloatingPortal>
          <div
            ref={setFloating}
            style={floatingStyles}
            className="
              pointer-events-none z-50 rounded-lg border border-border-default bg-bg-elevated px-3 py-2 text-sm
              text-text-primary shadow-lg
            "
          >
            <div className="font-semibold" style={{ color: activeColors[hoveredSegment.type] }}>
              {SEGMENT_INFO[hoveredSegment.type].label}
            </div>
            <div className="mt-1 text-text-disabled">
              {format(hoveredSegment.startDate, 'MMM d, yyyy')} → {format(hoveredSegment.endDate, 'MMM d, yyyy')}
            </div>
            <div className="mt-1 text-xs text-text-muted">
              Probability: {SEGMENT_INFO[hoveredSegment.type].probability}
            </div>
            <div className="mt-1 border-t border-border-default pt-1 text-xs text-text-muted">
              {hoveredSegment.deliverableName}
            </div>
          </div>
        </FloatingPortal>
      )}

      {/* Dependency Popover */}
      {activeDependencyInfo && (
        <DependencyPopover
          deliverableName={activeDependencyInfo.deliverable.name}
          incoming={activeDependencyInfo.incoming}
          outgoing={activeDependencyInfo.outgoing}
          referenceElement={dependencyBadgeRef}
          isOpen={activeDependencyId !== null}
          onClose={handleCloseDependencyPopover}
          onNavigate={handleDependencyNavigate}
        />
      )}
    </div>
  );
}
